import './submittableInput.scss';
import React, {KeyboardEventHandler, useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import classNames from 'clsx';
import {Spinner} from '../spinner/spinner';
import {Button, Modal} from 'react-bootstrap';
import {Keyname} from '../../utils/utils';
import {renderActionButtons} from './submittable-util';

export type FormFieldType = 'line' | 'multiline' | 'number' | 'boolean';

type InputProps = {
    fieldType: FormFieldType;
    minDisplayHeight?: number;
    initialValue: string;
    validated?: boolean;
    onSubmit: (newValue: string) => void;
    onCancel: () => void;
    onConfirm?: (value: string) => void;
    onRemove?: () => void;
};

export const SubmittableInput = React.memo((props: InputProps) => {
    const {t} = useTranslation();
    const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
    const [localValue, setLocalValue] = useState<string>(props.initialValue);
    const [showSaveModal, setShowSaveModal] = useState<boolean>(false);

    useCallback(() => {
        setLoadingUpdate(false);
    }, []);

    const onChange = (e) => {
        setLocalValue(e.target.value);
    };

    let skipBlurEvent = false;
    let timeout;
    const onSubmit = () => {
        skipBlurEvent = true;
        setLoadingUpdate(true);
        if (localValue !== props.initialValue) {
            if (timeout) clearTimeout(timeout);
            props.onSubmit(localValue);
        } else {
            props.onCancel();
        }
        setLoadingUpdate(false);

    };
    const minHeight = props.minDisplayHeight || 32;

    const renderNumberField = () => (
        <input
            autoFocus
            disabled={loadingUpdate}
            onChange={onChange}
            type="number"
            value={localValue}
            onKeyDown={onTextAreaKeyDown}
            onBlur={onBlur}
        />
    );
    const onBlur = () => {
        if (skipBlurEvent) return;

        if (localValue !== props.initialValue) {
            timeout = setTimeout(() => {
                setShowSaveModal(true);
            }, 300);
        } else {
            props.onCancel();
        }
    };

    const onRemove = () => {
        if (props.onRemove) {
            props.onRemove();
        }
    };
    const renderTextField = () => (
        <textarea
            style={{minHeight: minHeight + 'px'}}
            autoFocus
            disabled={loadingUpdate}
            onChange={onChange}
            placeholder=""
            value={localValue}
            onKeyDown={onTextAreaKeyDown}
            onBlur={onBlur}
        />
    );
    const onTextAreaKeyDown: KeyboardEventHandler = (e) => {
        if (e.key === Keyname.ENTER && (props.fieldType !== 'multiline' || e.ctrlKey)) {
            onSubmit();
            e.stopPropagation();
            (e.currentTarget as any).blur();
        }
    };

    const renderSaveModal = () => (
        <Modal show={showSaveModal} centered>
            <Modal.Header>
                <Modal.Title>{t('submittable-input.modal.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{t('submittable-input.modal.text')}</Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={handleSaveModalDiscard}>
                    {t('modal.discard')}
                </Button>
                <Button variant="primary" onClick={handleSaveModalSave}>
                    {t('general.save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
    const handleSaveModalDiscard = () => {
        props.onCancel();
        setShowSaveModal(false);
    };
    const handleSaveModalSave = () => {
        onSubmit();
        setShowSaveModal(false);
    };

    const renderSpinner = () => (
        <div className={'submit-update-indicator'}>
            <Spinner/>
        </div>
    );


    const onConfirm = () => {
        if (props.onConfirm) {
            props.onConfirm(localValue);
        }
    };
    return (
        <div className={classNames('submittable-input', props.fieldType, loadingUpdate ? 'loading-update' : '')}>
            {props.fieldType === 'number' && renderNumberField()}
            {props.fieldType === 'line' && renderTextField()}
            {props.fieldType === 'multiline' && renderTextField()}

            {loadingUpdate ? renderSpinner() : renderActionButtons(onRemove, t, props.validated, onConfirm)}
            {renderSaveModal()}
        </div>
    );
});
